import React, { useState, useEffect, useRef } from 'react';
import styles from './EntitlementFieldLobbyResults.module.scss';
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useStats,
} from 'react-instantsearch';
import { ZAKAUT, leumitSearchClient } from 'assets/constants';
import strings from "assets/strings";
import Text from "components/text/Text";
import { EntitlementFieldLobbyResult } from './EntitlementFieldLobbyResult';
import type { BaseHit, Hit } from 'instantsearch.js';
import { EntitlementResultType } from 'types';
import { VirtualRefinementList } from 'components/virtualRefinement/VirtualRefinmentList';
import SvgGenerator from 'components/svgGenerator/SvgGenerator';
import { Autocomplete } from 'components/inputs/autocomplete/Autocomplete';
import type { ProductItem } from 'components/inputs/autocomplete/Autocomplete';

function NoResults() {
  const noResultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noResultsRef.current) {
      noResultsRef.current.focus();
    }
  }, []);

  return (
    <div
      className={styles.noMessagesWrapper}
      role="status"
      aria-live="assertive"
      tabIndex={-1}
      ref={noResultsRef}
    >
      <SvgGenerator name="Magnifier" className="magnifierIcon" objectName="doctorSearchResults" />
      <Text text={strings.globalStrings.noResultsFound} />
    </div>
  );
}

const isSilverGoldHit = (hit: any) => {
  const z = hit?.zakauts;
  if (!z) return false;

  const hasSilver =
    Object.prototype.hasOwnProperty.call(z, "silver") &&
    (z.silver?.length ?? 0) > 0;

  const hasGolden =
    Object.prototype.hasOwnProperty.call(z, "golden") &&
    (z.golden?.length ?? 0) > 0;

  return hasSilver || hasGolden;
};


function InfiniteHitsComponent({ onlySilverGold }: { onlySilverGold: boolean }) {
  const { hits } = useInfiniteHits();

  const finalHits = onlySilverGold ? hits.filter(isSilverGoldHit) : hits;
  if (!finalHits.length) return null;

  return (
    <div>
      {finalHits.map((hit: Hit<BaseHit>) => (
        <EntitlementFieldLobbyResult key={hit.objectID} hit={hit as EntitlementResultType} />
      ))}
    </div>
  );
}



function SearchResultsView({ onlySilverGold }: { onlySilverGold: boolean }) {
  const { results, status } = useInstantSearch();
  const { hits } = useInfiniteHits();

  const extendedResults = results as typeof results & {
    __isError?: boolean;
    __errorStatus?: number;
  };

  if (extendedResults.__isError && extendedResults.__errorStatus === 403) {
    return (
      <div className={styles.noMessagesWrapper}>
        <Text text={strings.globalStrings.networkError} />
      </div>
    );
  }

  const finalHits = onlySilverGold ? hits.filter(isSilverGoldHit) : hits;

  if ((status === "idle" || status === "error") && finalHits.length === 0) {
    return <NoResults />;
  }

  if (finalHits.length === 0 && status !== "idle") {
    return null;
  }

  return <InfiniteHitsComponent onlySilverGold={onlySilverGold} />;
}

const getKeyParam = () => {
  // 1) Query רגיל: ?key=silvergold
  const search = window.location.search;
  if (search && search.startsWith("?")) {
    return new URLSearchParams(search).get("key");
  }

  // 2) Query בתוך hash: #/route?key=silvergold
  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex >= 0) {
    return new URLSearchParams(hash.substring(qIndex)).get("key");
  }

  return null;
};


export function EntitlementFieldLobbyResults() {
  const [fieldDataId, setFieldDataId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [onlySilverGold, setOnlySilverGold] = useState(() => {
    const key = getKeyParam();
    return key?.toLowerCase() === "silvergold";
  });

  useEffect(() => {
    const update = () => {
      const key = getKeyParam();
      const value = key?.toLowerCase() === "silvergold";
  
      console.warn("[EntitlementFieldLobbyResults key update]", {
        key,
        value,
        href: window.location.href,
        search: window.location.search,
        hash: window.location.hash,
      });
  
      setOnlySilverGold(value);
  
      // if (value) {
      //   // "הצג הכל" אוטומטי
      //   setSearchQuery("");
      //   setHasSubmitted(true);
      // }
    };
  
    update();
  
    window.addEventListener("popstate", update);
    window.addEventListener("hashchange", update);
  
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("hashchange", update);
    };
  }, []);
  

  useEffect(() => {
    const div = document.querySelector('[data-id]');
    if (div) {
      const dataAttr = div.getAttribute('data-id');
      setFieldDataId(dataAttr);
    }
  }, []);

  const Stats: React.FC = () => {
    const { nbHits } = useStats();
    return nbHits ? (
      <div className={styles.resultsCount}>
        <Text
          className={styles.title}
          text={`${strings.entitlementFieldLobby.found} ${nbHits} ${strings.entitlementFieldLobby.entitlements}${strings.entitlementFieldLobby.fieldResultName}`}
        />
      </div>
    ) : null;
  };

  const triggerSubmit = () => {
    setSearchQuery("");
    setHasSubmitted(true);
  };

  const handleItemSelect = (item: ProductItem) => {
    setSearchQuery(item.serviceName);
    setHasSubmitted(true);
  };

  return (
    <div className="row ais-InstantSearch">
      <InstantSearch indexName={ZAKAUT} searchClient={leumitSearchClient}>
        <div className="col-12 ais-SearchResults">
          <div className={styles.searchDiv}>
            <div className={styles.searchBox}>
              <Autocomplete
                searchClient={leumitSearchClient}
                indexName={ZAKAUT}
                onItemSelect={handleItemSelect}
                fieldsIdList={fieldDataId || undefined}
                restrictedLetters={/[^A-Za-z0-9א-ת()\s\-]/g}
                placeholder={strings.entitlementFieldLobby.placeholder}
              />
              <Text
                text={strings.entitlementFieldLobby.showAllEntitlements}
                className={styles.serachTitle}
                onClick={triggerSubmit}
              />
            </div>
          </div>

          {hasSubmitted && (
            <>
                <Configure
              query={searchQuery || undefined}
              snippetEllipsisText="…"
              hitsPerPage={1000}
              filters={fieldDataId ? `fieldsIdList:"${fieldDataId}"` : ""}
            />

              <Stats />
              <SearchResultsView onlySilverGold={onlySilverGold} />
            </>
          )}

          <VirtualRefinementList attribute="fieldsNameList" />
        </div>
      </InstantSearch>
    </div>
  );
}
